import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Chip,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Divider,
    Paper,
    Tooltip,
    CircularProgress,
    SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoIcon from '@mui/icons-material/Info';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import mqtt, { MqttClient } from 'mqtt';
import { v4 as uuidv4 } from 'uuid';
import { LpsInstallation, MqttCredentials } from '../lps.types';
import { getInstallationMqttCredentials } from '../lps.api';

// ── Command definitions ─────────────────────────────────────────────────

interface CommandDef {
    label: string;
    method: string;
    endpoint: string;
    params: string[];
    defaultData?: Record<string, any>;
}

const COMMANDS: CommandDef[] = [
    { label: 'Get Configurations', method: 'GET', endpoint: '/cuwbnets', params: [] },
    {
        label: 'Get Devices',
        method: 'GET',
        endpoint: '/cuwbnets/{configName}/devices',
        params: ['configName'],
    },
    {
        label: 'Get Config Status',
        method: 'GET',
        endpoint: '/cuwbnets/{configName}/status',
        params: ['configName'],
    },
    {
        label: 'Start Config',
        method: 'POST',
        endpoint: '/cuwbnets/{configName}',
        params: ['configName'],
        defaultData: { action: 'start' },
    },
    {
        label: 'Stop Config',
        method: 'POST',
        endpoint: '/cuwbnets/{configName}',
        params: ['configName'],
        defaultData: { action: 'stop' },
    },
    {
        label: 'Start Recording',
        method: 'POST',
        endpoint: '/recording/start',
        params: [],
        defaultData: { config_name: '', username: '' },
    },
    {
        label: 'Stop Recording',
        method: 'POST',
        endpoint: '/recording/stop',
        params: [],
        defaultData: { username: '' },
    },
    {
        label: 'Check Active Recording',
        method: 'GET',
        endpoint: '/recording/user/{username}/active',
        params: ['username'],
    },
    {
        label: 'Enable Tag',
        method: 'PATCH',
        endpoint: '/cuwbnets/{configName}/devices/{tagId}',
        params: ['configName', 'tagId'],
        defaultData: { disabled: false },
    },
    {
        label: 'Disable Tag',
        method: 'PATCH',
        endpoint: '/cuwbnets/{configName}/devices/{tagId}',
        params: ['configName', 'tagId'],
        defaultData: { disabled: true },
    },
    {
        label: 'Reset Tag',
        method: 'POST',
        endpoint: '/tags/{tagId}/reset',
        params: ['tagId'],
        defaultData: { action: 'reset' },
    },
    {
        label: 'Assign Athlete',
        method: 'POST',
        endpoint: '/tags/{tagId}/assign',
        params: ['tagId'],
        defaultData: { action: 'assign_athlete', athlete_id: '', athlete_name: '' },
    },
    {
        label: 'Unassign Athlete',
        method: 'POST',
        endpoint: '/tags/{tagId}/unassign',
        params: ['tagId'],
        defaultData: { action: 'unassign_athlete' },
    },
    {
        label: 'Start Tracking',
        method: 'POST',
        endpoint: '/tracking/start',
        params: [],
        defaultData: { action: 'start_tracking' },
    },
    {
        label: 'Stop Tracking',
        method: 'POST',
        endpoint: '/tracking/stop',
        params: [],
        defaultData: { action: 'stop_tracking' },
    },
    { label: 'Get System Status', method: 'GET', endpoint: '/status', params: [] },
];

const METHOD_COLORS: Record<string, string> = {
    GET: '#4caf50',
    POST: '#ff9800',
    PATCH: '#2196f3',
};

// ── Types ───────────────────────────────────────────────────────────────

interface DebugMessage {
    id: string;
    type: 'inbound' | 'outbound' | 'system' | 'error';
    text: string;
    timestamp: Date;
}

interface Props {
    open: boolean;
    onClose: () => void;
    installation: LpsInstallation;
}

// ── Component ───────────────────────────────────────────────────────────

const LpsMqttDebugDialog: React.FC<Props> = ({ open, onClose, installation }) => {
    const [connectionState, setConnectionState] = useState<string>('idle');
    const [credentials, setCredentials] = useState<MqttCredentials | null>(null);
    const [messages, setMessages] = useState<DebugMessage[]>([]);
    const [subscribedTopics, setSubscribedTopics] = useState<string[]>([]);
    const [selectedCommand, setSelectedCommand] = useState(0);
    const [paramValues, setParamValues] = useState<Record<string, string>>({});
    const [bodyText, setBodyText] = useState('');
    const [sending, setSending] = useState(false);
    const clientRef = useRef<MqttClient | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const addMessage = useCallback((type: DebugMessage['type'], text: string) => {
        setMessages((prev) => [
            { id: uuidv4(), type, text, timestamp: new Date() },
            ...prev.slice(0, 199),
        ]);
    }, []);

    // Connect on dialog open
    useEffect(() => {
        if (!open) return;
        connect();
        return () => {
            disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Update body when command changes
    useEffect(() => {
        const cmd = COMMANDS[selectedCommand];
        const newParams: Record<string, string> = {};
        cmd.params.forEach((p) => (newParams[p] = paramValues[p] || ''));
        setParamValues(newParams);
        setBodyText(cmd.defaultData ? JSON.stringify(cmd.defaultData, null, 2) : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCommand]);

    const connect = async () => {
        setConnectionState('fetching_credentials');
        addMessage('system', `Fetching MQTT credentials for "${installation.name}"...`);

        try {
            const res = await getInstallationMqttCredentials(installation.uuid);
            const creds = res.data;
            setCredentials(creds);
            setConnectionState('connecting');
            addMessage(
                'system',
                `Credentials received. Endpoint: ${creds.endpoint}, Device: ${creds.device_name}`,
            );
            addMessage('outbound', `Connecting to ${creds.url.substring(0, 80)}...`);

            const client = mqtt.connect(creds.url, {
                protocolVersion: 4,
                clean: true,
                reconnectPeriod: 0,
            });

            clientRef.current = client;

            client.on('connect', () => {
                setConnectionState('connected');
                const responseTopic = `responses/${creds.device_name}`;
                client.subscribe(responseTopic);
                setSubscribedTopics([responseTopic]);
                addMessage('system', `Connected! Subscribed to: ${responseTopic}`);
                addMessage('system', `Command topic: commands/${creds.device_name}`);
            });

            client.on('message', (_topic, payload) => {
                const raw = payload.toString();
                try {
                    const parsed = JSON.parse(raw);
                    addMessage('inbound', JSON.stringify(parsed));
                } catch {
                    addMessage('inbound', raw);
                }
            });

            client.on('error', (err) => {
                addMessage('error', `MQTT error: ${err.message}`);
            });

            client.on('close', () => {
                setConnectionState('disconnected');
                setSubscribedTopics([]);
                addMessage('system', 'Disconnected');
            });
        } catch (err: any) {
            setConnectionState('error');
            addMessage('error', `Failed: ${err?.response?.data?.message || err?.message || err}`);
        }
    };

    const disconnect = () => {
        if (clientRef.current) {
            clientRef.current.end(true);
            clientRef.current = null;
        }
        setConnectionState('idle');
        setSubscribedTopics([]);
    };

    const sendCommand = async () => {
        if (!clientRef.current || !credentials) return;

        const cmd = COMMANDS[selectedCommand];
        let endpoint = cmd.endpoint;
        Object.entries(paramValues).forEach(([k, v]) => {
            endpoint = endpoint.replace(`{${k}}`, v);
        });

        let data: Record<string, any> = {};
        if (bodyText.trim()) {
            try {
                data = JSON.parse(bodyText);
            } catch {
                addMessage('error', 'Invalid JSON in body');
                return;
            }
        }

        const message = {
            message_id: uuidv4(),
            timestamp: Date.now() / 1000,
            user_id: 'admin',
            command: { method: cmd.method, endpoint, data },
        };

        const topic = `commands/${credentials.device_name}`;
        setSending(true);
        addMessage(
            'outbound',
            `${cmd.method} ${endpoint}${
                Object.keys(data).length ? ` ${JSON.stringify(data)}` : ''
            }`,
        );

        clientRef.current.publish(topic, JSON.stringify(message), { qos: 1 }, (err) => {
            setSending(false);
            if (err) addMessage('error', `Publish failed: ${err.message}`);
        });
    };

    const handleClose = () => {
        disconnect();
        setMessages([]);
        setCredentials(null);
        onClose();
    };

    const statusColor =
        connectionState === 'connected'
            ? '#4caf50'
            : connectionState === 'error'
            ? '#f44336'
            : connectionState === 'connecting' || connectionState === 'fetching_credentials'
            ? '#ff9800'
            : '#9e9e9e';

    const cmd = COMMANDS[selectedCommand];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{ sx: { height: '85vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: statusColor,
                        boxShadow: `0 0 6px ${statusColor}`,
                    }}
                />
                <Typography variant="h6" sx={{ flex: 1 }}>
                    MQTT Debug — {installation.name}
                </Typography>
                {credentials && (
                    <Chip size="small" label={credentials.device_name} variant="outlined" />
                )}
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ display: 'flex', gap: 2, p: 2, overflow: 'hidden' }}>
                {/* Left: Command Tester */}
                <Box
                    sx={{
                        width: 420,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    <Typography variant="subtitle2">Command Tester</Typography>

                    <FormControl size="small" fullWidth>
                        <InputLabel>Command</InputLabel>
                        <Select
                            value={selectedCommand}
                            label="Command"
                            onChange={(e: SelectChangeEvent<number>) =>
                                setSelectedCommand(Number(e.target.value))
                            }
                        >
                            {COMMANDS.map((c, i) => (
                                <MenuItem key={i} value={i}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            label={c.method}
                                            size="small"
                                            sx={{
                                                bgcolor: `${METHOD_COLORS[c.method]}22`,
                                                color: METHOD_COLORS[c.method],
                                                fontWeight: 700,
                                                fontSize: 10,
                                                height: 20,
                                            }}
                                        />
                                        <span>{c.label}</span>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Paper
                        variant="outlined"
                        sx={{
                            px: 1.5,
                            py: 1,
                            fontFamily: 'monospace',
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Chip
                            label={cmd.method}
                            size="small"
                            sx={{
                                bgcolor: `${METHOD_COLORS[cmd.method]}22`,
                                color: METHOD_COLORS[cmd.method],
                                fontWeight: 700,
                                fontSize: 10,
                                height: 20,
                            }}
                        />
                        <span>{cmd.endpoint}</span>
                    </Paper>

                    {cmd.params.map((param) => (
                        <TextField
                            key={param}
                            size="small"
                            label={param}
                            value={paramValues[param] || ''}
                            onChange={(e) =>
                                setParamValues((prev) => ({ ...prev, [param]: e.target.value }))
                            }
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        />
                    ))}

                    {(cmd.defaultData || cmd.method !== 'GET') && (
                        <TextField
                            size="small"
                            label="Body (JSON)"
                            multiline
                            minRows={4}
                            maxRows={8}
                            value={bodyText}
                            onChange={(e) => setBodyText(e.target.value)}
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
                        />
                    )}

                    <Button
                        variant="contained"
                        startIcon={
                            sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />
                        }
                        onClick={sendCommand}
                        disabled={connectionState !== 'connected' || sending}
                        fullWidth
                    >
                        {sending ? 'Sending...' : 'Send Command'}
                    </Button>

                    <Divider sx={{ my: 1 }} />

                    {/* Connection info */}
                    {credentials && (
                        <Box
                            sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}
                        >
                            <div>
                                <b>Endpoint:</b> {credentials.endpoint}
                            </div>
                            <div>
                                <b>Device:</b> {credentials.device_name}
                            </div>
                            <div>
                                <b>Expires:</b>{' '}
                                {new Date(credentials.expiration).toLocaleTimeString()}
                            </div>
                        </Box>
                    )}

                    {/* Subscribed topics */}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: 1, fontWeight: 700 }}
                    >
                        SUBSCRIBED TOPICS
                    </Typography>
                    {subscribedTopics.length === 0 ? (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">
                            None
                        </Typography>
                    ) : (
                        subscribedTopics.map((t) => (
                            <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: '#4caf50',
                                    }}
                                />
                                <Typography variant="caption" fontFamily="monospace">
                                    {t}
                                </Typography>
                            </Box>
                        ))
                    )}

                    {connectionState === 'disconnected' || connectionState === 'error' ? (
                        <Button size="small" onClick={connect}>
                            Reconnect
                        </Button>
                    ) : connectionState === 'connected' ? (
                        <Button size="small" color="inherit" onClick={disconnect}>
                            Disconnect
                        </Button>
                    ) : null}
                </Box>

                {/* Right: Message Log */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">Messages</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {messages.length}
                        </Typography>
                    </Box>
                    <Paper
                        variant="outlined"
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: 1,
                            bgcolor: 'background.default',
                            fontFamily: 'monospace',
                            fontSize: 11,
                        }}
                    >
                        {messages.length === 0 ? (
                            <Typography variant="caption" color="text.disabled" fontStyle="italic">
                                No messages yet
                            </Typography>
                        ) : (
                            messages.map((msg) => <MessageEntry key={msg.id} message={msg} />)
                        )}
                        <div ref={messagesEndRef} />
                    </Paper>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

// ── Message Entry with collapsible JSON ─────────────────────────────────

const MessageEntry: React.FC<{ message: DebugMessage }> = ({ message }) => {
    const [expanded, setExpanded] = useState(false);

    const iconMap = {
        inbound: <ArrowDownwardIcon sx={{ fontSize: 12, color: '#2196f3' }} />,
        outbound: <ArrowUpwardIcon sx={{ fontSize: 12, color: '#4caf50' }} />,
        error: <ErrorOutlineIcon sx={{ fontSize: 12, color: '#f44336' }} />,
        system: <InfoIcon sx={{ fontSize: 12, color: '#9e9e9e' }} />,
    };

    const time = message.timestamp.toLocaleTimeString('en-US', {
        hour12: false,
        fractionalSecondDigits: 3,
    } as any);

    // Try to parse JSON
    let formattedJson: string | null = null;
    let prefixText = message.text;
    const braceIdx = message.text.indexOf('{');
    const bracketIdx = message.text.indexOf('[');
    let jsonStart = -1;
    if (braceIdx >= 0 && bracketIdx >= 0) jsonStart = Math.min(braceIdx, bracketIdx);
    else if (braceIdx >= 0) jsonStart = braceIdx;
    else if (bracketIdx >= 0) jsonStart = bracketIdx;

    if (jsonStart >= 0) {
        try {
            const parsed = JSON.parse(message.text.substring(jsonStart));
            formattedJson = JSON.stringify(parsed, null, 2);
            prefixText = jsonStart > 0 ? message.text.substring(0, jsonStart).trim() : '';
        } catch {
            /* not json */
        }
    }

    const hasJson = formattedJson !== null;

    return (
        <Box sx={{ py: 0.25 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.5,
                    cursor: hasJson ? 'pointer' : 'default',
                }}
                onClick={hasJson ? () => setExpanded(!expanded) : undefined}
            >
                {iconMap[message.type]}
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.disabled',
                        fontFamily: 'monospace',
                        fontSize: 10,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {time}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: 11,
                        color: message.type === 'error' ? '#f44336' : 'text.primary',
                        flex: 1,
                        wordBreak: 'break-all',
                    }}
                >
                    {hasJson && !expanded
                        ? prefixText
                            ? `${prefixText} {..}`
                            : '{..}'
                        : hasJson
                        ? prefixText
                        : message.text}
                </Typography>
                {hasJson &&
                    (expanded ? (
                        <ExpandLessIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    ) : (
                        <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    ))}
                <Tooltip title="Copy">
                    <IconButton
                        size="small"
                        sx={{ p: 0 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(message.text);
                        }}
                    >
                        <ContentCopyIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                    </IconButton>
                </Tooltip>
            </Box>
            {hasJson && expanded && (
                <Paper
                    variant="outlined"
                    sx={{
                        ml: 3,
                        mt: 0.5,
                        mb: 0.5,
                        p: 1,
                        bgcolor: 'action.hover',
                        fontFamily: 'monospace',
                        fontSize: 11,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        cursor: 'pointer',
                    }}
                    onClick={() => navigator.clipboard.writeText(formattedJson!)}
                >
                    {formattedJson}
                </Paper>
            )}
        </Box>
    );
};

export default LpsMqttDebugDialog;
